import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, LOCALE_ID, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';

import { ExchangeService } from 'src/app/services/exchange-rate.service';
import { ExchangeRateDataset } from 'src/app/types/exchange-rates';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatomoModule, MatomoTrackerDirective } from 'ngx-matomo-client';

@Component({
    selector: 'app-exchange-rate-calculator',
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        MatDividerModule,
        MatAutocompleteModule,
        MatTooltipModule,
        MatomoModule,
        MatomoTrackerDirective,
        ReactiveFormsModule
    ],
    templateUrl: './exchange-rate-calculator.component.html',
    styleUrls: ['./exchange-rate-calculator.component.scss'],
    standalone: true
})
export class ExchangeRateCalculatorComponent implements OnInit {
    // Variables and inital values //
    publisherSingleDataset: ExchangeRateDataset[] = [];
    publishers: any[] = [];
    selectedPublisherId: string | null = null;
    publishersLoaded: boolean = false;
    sourceCurrency: string = 'EUR';
    targetCurrency: string = '';
    sourceAmount: number = 1000;
    formattedSourceAmount: string = '';
    markup: number = 0;
    markupAmount: number = 0;
    totalWithMarkup: number | null = null;
    markupVisible: boolean = false;
    conversionRate: number | null = null;
    convertedAmount: number | null = null;
    error: any;
    targetCurrenciesList: string[] = [];
    sourceCurrenciesList: string[] = [];
    unitExchangeRate: number | null = null;
    unitExchangeRateWithMarkup: number | null = null;
    sourceCurrencyInput: string = '';
    filteredCurrencies: string[] = [];
    isReversedConversion: boolean = false;

    /** handle currency form field errors  */
    currency = new FormControl('');
    errorMessage = signal('');

    /**
     * Template reference for the dialog used in the component.
     */
    @ViewChild('dialogTemplate') dialogTemplate!: TemplateRef<any>;
    @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

    constructor(
        private exchangeService: ExchangeService, // Service for fetching exchange-related data
        public dialog: MatDialog, // Material Dialog service for displaying modal dialogs
        private cdr: ChangeDetectorRef, // Change detector for manually triggering view updates
        @Inject(LOCALE_ID) public locale: string // Current locale for formatting and internationalization
    ) {}

    // ------------------ GETTERS ------------------ //

    /**
     * Retrieves a unique list of source currencies available in the dataset.
     * @returns {string[]} - Array of unique source currencies.
     */
    get sourceCurrencies(): string[] {
        return Array.from(new Set(this.publisherSingleDataset.map((item) => item.sourceCurrency)));
    }

    /**
     * Gets the converted amount to be displayed.
     * Defaults to 0 if no converted amount is available.
     * @returns {number} - The displayed converted amount.
     */
    get displayedConvertedAmount(): number {
        return this.convertedAmount ?? 0;
    }

    /**
     * Determines the displayed source currency.
     * If conversion is reversed, shows the target currency instead.
     * @returns {string} - The displayed source currency.
     */
    get displayedSourceCurrency(): string {
        return this.isReversedConversion ? this.sourceCurrency : this.targetCurrency;
    }

    /**
     * Determines the displayed target currency.
     * If conversion is reversed, shows the source currency instead.
     * @returns {string} - The displayed target currency.
     */
    get displayedTargetCurrency(): string {
        return this.isReversedConversion ? this.targetCurrency : this.sourceCurrency;
    }

    /**
     * Determines the suffix for displayed currencies based on conversion direction.
     * If conversion is reversed, shows the source currency.
     * @returns {string} - The displayed currency suffix.
     */
    get displayedCurrencySuffix(): string {
        return this.isReversedConversion ? this.sourceCurrency : this.targetCurrency;
    }

    /**
     * Determines the sign to display based on whether markup is active or not.
     * @returns {string} - '+' if markup is being added, '-' if markup is being removed.
     */
    get markupSign(): string {
        return this.markupVisible ? '+' : '-';
    }

    /**
     * Check if the selected currency is valid or invalid to handle errors
     *
     * @readonly
     * @type {boolean}
     */
    get isCurrencyInvalid(): boolean {
        return this.currency.hasError('invalid');
    }

    // ------------------ LIFECYCLE HOOKS ------------------ //

    /**
     * Lifecycle hook that runs after component initialization.
     * Resets initial values, loads publishers, and sets up default currencies.
     * @returns {void}
     */
    ngOnInit(): void {
        // Reset key values to their defaults
        this.resetValues();

        // Load the list of publishers and their exchange rates
        this.loadPublishers();

        // Set default source currency to USD
        this.sourceCurrency = 'USD';

        // Update the formatted display of the source amount
        this.updateFormattedSourceAmount();

        // Initialize the list of filtered currencies
        this.filteredCurrencies = this.sourceCurrencies;

        // Set the input field to display the full name and code of the source currency
        this.sourceCurrencyInput = this.getCurrencyFullNameWithCode(this.sourceCurrency);

        // Clear the target currency display until rates are fully loaded
        this.targetCurrency = '';
    }

    /**
     * Loads the list of publishers and sets the default publisher if available.
     * Fetches the exchange rates for the default publisher or logs a warning if not found.
     * @returns {void}
     */
    loadPublishers(): void {
        this.publishersLoaded = false;

        this.exchangeService.getPublishers().subscribe({
            next: (publishers) => {
                // Sort publishers alphabetically by name or description
                this.publishers = publishers.sort((a, b) => a.description.localeCompare(b.description, this.locale));

                // Set default publisher if available
                const defaultPublisher = this.publishers.find((p) => p.id === 'EUCB');
                if (defaultPublisher) {
                    this.selectedPublisherId = defaultPublisher.id;
                    this.loadExchangeRates(); // Load exchange rates for the default publisher
                } else {
                    console.warn('EUCB publisher not found. Reload Page please or delete cookies.');
                }

                this.publishersLoaded = true;
            },
            error: (err) => {
                console.error('Error fetching publishers:', err);
                this.publishersLoaded = false;
            }
        });
    }

    /**
     * Loads the exchange rates for the currently selected publisher.
     * Updates the target currency and ensures the source currency is valid.
     * Calculates the rate after loading the data.
     * @returns {void}
     */
    loadExchangeRates(): void {
        if (!this.selectedPublisherId) return;

        this.targetCurrency = ''; // Keep blank until data is loaded

        this.exchangeService.getExchangeRates(this.selectedPublisherId).subscribe({
            next: (response: ExchangeRateDataset[]) => {
                this.publisherSingleDataset = response;

                // Set `targetCurrency` only after data is received
                const newTargetCurrency = response[0]?.targetCurrency || '';
                if (newTargetCurrency) {
                    this.targetCurrency = newTargetCurrency;
                }

                // Ensure sourceCurrency is valid
                const availableSourceCurrencies = this.sourceCurrencies;
                if (!availableSourceCurrencies.includes(this.sourceCurrency) && !this.sourceCurrencyInput.trim()) {
                    this.sourceCurrency = 'USD'; // Default to USD
                    this.sourceCurrencyInput = this.getCurrencyFullNameWithCode(this.sourceCurrency); // Update the input field
                }

                this.updateFormattedSourceAmount();
                this.calculateRate();
            },
            error: (err) => {
                this.error = err;
                console.error('Error fetching exchange rates:', err);
            }
        });
    }

    /**
     * Calculates the exchange rate between the source and target currencies.
     * Applies either normal or reversed conversion depending on the conversion direction.
     * Updates the view to reflect the calculated rate.
     * Resets values if no matching rate entry is found.
     * @returns {void}
     */
    calculateRate(): void {
        const effectiveSourceCurrency = this.sourceCurrency;
        const effectiveTargetCurrency = this.targetCurrency;

        const rateEntry = this.publisherSingleDataset.find(
            (rate) => rate.sourceCurrency === effectiveSourceCurrency && rate.targetCurrency === effectiveTargetCurrency
        );

        if (rateEntry) {
            const rawExchangeRate = rateEntry.rate;

            if (this.isReversedConversion) {
                this.applyReversedConversion(rawExchangeRate, rateEntry.mathematicOperator);
            } else {
                this.applyNormalConversion(rawExchangeRate, rateEntry.mathematicOperator);
            }

            this.cdr.detectChanges();
        } else {
            console.warn('No matching rate entry found for:', {
                sourceCurrency: effectiveSourceCurrency,
                targetCurrency: effectiveTargetCurrency
            });
            this.resetValues();
        }
    }

    /**
     * Switches the source and target currencies for conversion.
     * Ensures that the source and target currencies are not the same after switching.
     * Recalculates the exchange rate after switching.
     * @returns {void}
     */
    switchCurrencies(): void {
        this.isReversedConversion = !this.isReversedConversion;

        if (this.sourceCurrency === this.targetCurrency) {
            this.sourceCurrency = this.sourceCurrencies.find((currency) => currency !== this.targetCurrency) || 'USD';
        }

        this.calculateRate();
    }

    /**
     * Applies normal conversion using the raw exchange rate and mathematical operator.
     * Calculates converted amount, unit exchange rate, and markup-related values.
     * @param {number} rawExchangeRate - The raw exchange rate.
     * @param {string} mathematicOperator - The operator ('DIVIDE' or 'MULTIPLY') for conversion.
     * @returns {void}
     */
    applyNormalConversion(rawExchangeRate: number, mathematicOperator: string): void {
        this.unitExchangeRate = mathematicOperator === 'DIVIDE' ? rawExchangeRate : 1 / rawExchangeRate;

        this.convertedAmount = this.sourceAmount * this.unitExchangeRate;

        this.unitExchangeRateWithMarkup = this.unitExchangeRate * (1 + this.markup / 100);

        this.totalWithMarkup = this.sourceAmount * this.unitExchangeRateWithMarkup;

        this.markupAmount = this.totalWithMarkup - this.convertedAmount;
    }

    /**
     * Applies reversed conversion using the raw exchange rate and mathematical operator.
     * Calculates reversed conversion values including unit exchange rate and markup.
     * @param {number} rawExchangeRate - The raw exchange rate.
     * @param {string} mathematicOperator - The operator ('DIVIDE' or 'MULTIPLY') for conversion.
     * @returns {void}
     */
    applyReversedConversion(rawExchangeRate: number, mathematicOperator: string): void {
        this.unitExchangeRate = mathematicOperator === 'DIVIDE' ? 1 / rawExchangeRate : rawExchangeRate;

        this.convertedAmount = this.sourceAmount * this.unitExchangeRate;

        this.unitExchangeRateWithMarkup = this.unitExchangeRate * (1 + this.markup / 100);

        this.totalWithMarkup = this.convertedAmount * (1 + this.markup / 100);

        this.markupAmount = Math.abs(this.totalWithMarkup - this.convertedAmount);
    }

    /**
     * Resets all key conversion-related values to their default state.
     * @returns {void}
     */
    resetValues(): void {
        this.conversionRate = null;
        this.convertedAmount = null;
        this.markupAmount = 0;
        this.totalWithMarkup = null;
        this.unitExchangeRate = null;
    }

    /**
     * Formats a numeric amount into a currency string with optional currency code.
     * Handles locale-specific formatting.
     * @param {number | null} amount - The numeric amount to format.
     * @param {string} [currencyCode] - The optional currency code.
     * @returns {string} - The formatted currency string.
     */
    formatCurrencyAmount(amount: number | null, currencyCode?: string): string {
        if (amount === null) return '0';
        if (currencyCode) {
            return new Intl.NumberFormat(this.locale, {
                style: 'currency',
                currency: currencyCode,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(amount);
        } else {
            return new Intl.NumberFormat(this.locale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(amount);
        }
    }

    /**
     * Formats a numeric amount without a currency symbol.
     * Handles locale-specific formatting.
     * @param {number} amount - The numeric amount to format.
     * @returns {string} - The formatted string.
     */
    formatAmountWithoutCurrency(amount: number): string {
        return new Intl.NumberFormat(this.locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount);
    }

    /**
     * Formats the exchange rate to a localized string with 4 decimal places.
     * @param {number | null} rate - The exchange rate to format.
     * @returns {string} - The formatted exchange rate or '0.0000' if null.
     */
    formatExchangeRate(rate: number | null): string {
        return new Intl.NumberFormat(this.locale, {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4
        }).format(rate || 0);
    }

    /**
     * Retrieves the description and target currency code for the selected publisher.
     * @param {string | null} publisherId - The ID of the publisher.
     * @returns {string} - The description and currency code or an empty string if not found.
     */
    getSelectedPublisherDescriptionWithCurrencyCode(publisherId: string | null): string {
        if (!publisherId) return '';
        const selectedPublisher = this.publishers.find((publisher) => publisher.id === publisherId);
        if (selectedPublisher) {
            return `${selectedPublisher.description} (${selectedPublisher.targetCurrency || ''})`;
        }
        return '';
    }

    /**
     * Retrieves the full name of a currency from its code.
     * Uses locale-specific formatting to find the full name.
     * @param {string} currencyCode - The currency code.
     * @returns {string} - The full currency name or the code if not found.
     */
    getCurrencyFullName(currencyCode: string): string {
        if (!currencyCode || currencyCode.trim() === '') {
            return ''; // Return empty string for invalid or empty currency codes
        }

        try {
            const displayNames = new Intl.DisplayNames([this.locale], { type: 'currency' });
            return displayNames.of(currencyCode) || currencyCode;
        } catch (error) {
            console.warn(`Unable to find full name for currency code: ${currencyCode}`, error);
            return currencyCode; // Return the currency code as fallback
        }
    }

    /**
     * Retrieves the full name of a currency along with its code.
     * @param {string} currencyCode - The currency code.
     * @returns {string} - The full name and code in the format "Name - Code".
     */
    getCurrencyFullNameWithCode(currencyCode: string): string {
        const fullName = this.getCurrencyFullName(currencyCode);
        return `${currencyCode} - ${fullName}`;
    }

    /**
     * Handles the keydown event for the input field.
     * Triggers the selection of the first currency when the "Enter" key is pressed.
     * Prevents default behavior of the key press to ensure custom handling.
     *
     * @param {KeyboardEvent} event - The keyboard event triggered by the user.
     */
    handleKeyDown(event: KeyboardEvent): void {
        // Check if the key is "Enter" or "Tab"
        if (event.key === 'Enter') {
            this.selectFirstCurrency();
            event.preventDefault(); // Prevent default behavior (especially for Tab)
        }
    }

    /**
     * Selects the first currency from the filtered list of currencies.
     * Updates the input field with the selected currency and triggers the selection logic.
     * Closes the autocomplete panel after the currency is selected.
     */
    selectFirstCurrency(): void {
        if (this.filteredCurrencies && this.filteredCurrencies.length > 0) {
            const firstCurrency = this.filteredCurrencies[0];
            this.sourceCurrencyInput = firstCurrency; // Update the input field
            this.onSourceCurrencySelected({ option: { value: firstCurrency } }); // Trigger selection logic

            // Close the autocomplete panel
            this.autocompleteTrigger.closePanel();
        }
    }

    /**
     * Updates the formatted source amount by formatting the numeric value without a currency symbol.
     * @returns {void}
     */
    updateFormattedSourceAmount(): void {
        this.formattedSourceAmount = this.formatAmountWithoutCurrency(this.sourceAmount) || '';
    }

    /**
     * Handles changes to the publisher selection.
     * Updates the selected publisher ID, resets the target currency, and reloads exchange rates.
     * @param {MatSelectChange} event - The event triggered by selecting a new publisher.
     * @returns {void}
     */
    onPublisherChange(event: MatSelectChange): void {
        const newPublisherId = event.value;
        if (newPublisherId) {
            this.selectedPublisherId = newPublisherId;
            this.targetCurrency = ''; // Blank out the currency symbol during the update
            this.cdr.detectChanges();
            this.loadExchangeRates();
        }
    }

    /**
     * Handles changes to the source amount input.
     * Parses the input, updates the source amount, and recalculates the exchange rate.
     * Preserves both thousands separators and trailing zeroes for decimals.
     *
     * @param {Event} event - The event triggered by the input change.
     * @returns {void}
     */
    onSourceAmountChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        let rawValue = input.value;

        // Detect decimal and group separators based on locale
        const decimalSeparator = (1.1).toLocaleString(this.locale).substring(1, 2); // Locale-specific decimal separator
        const groupSeparator = decimalSeparator === ',' ? '.' : ','; // Opposite separator for thousands

        // Step 1: Sanitize input
        rawValue = rawValue
            .replace(new RegExp(`[^0-9${decimalSeparator}]`, 'g'), '') // Remove invalid characters
            .replace(new RegExp(`\\${decimalSeparator}(.*)\\${decimalSeparator}`, 'g'), `$1`); // Allow only one decimal separator

        // Step 2: Limit digits after the decimal separator
        const parts = rawValue.split(decimalSeparator);

        if (parts.length > 1 && parts[1].length > 2) {
            parts[1] = parts[1].substring(0, 2); // Keep only two digits after the separator
        }

        rawValue = parts.join(decimalSeparator);

        // Step 3: Parse the numeric value for calculations (strip separators for parsing)
        const numericValue = parseFloat(
            rawValue.replace(new RegExp(`\\${groupSeparator}`, 'g'), '').replace(decimalSeparator, '.')
        );
        this.sourceAmount = !isNaN(numericValue) ? numericValue : 0;

        // Step 4: Determine the number of fraction digits to display
        const fractionDigits = parts[1] ? parts[1].length : 0;

        // Step 5: Format the value with locale-specific grouping and decimal separators
        let formattedValue = new Intl.NumberFormat(this.locale, {
            useGrouping: true, // Add thousands separator
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits
        }).format(this.sourceAmount);

        // Step 6: Preserve the decimal separator if the user ends with it
        if (rawValue.endsWith(decimalSeparator)) {
            formattedValue += decimalSeparator;
        }

        // Step 7: Update the input field and trigger calculations
        input.value = formattedValue; // Update the input field with the formatted value
        this.calculateRate();
    }

    /**
     * Filters the list of currencies based on the user's search input.
     * Matches the search term against currency names and codes.
     * @param {string} value - The search term entered by the user.
     * @returns {void}
     */
    onCurrencySearchChange(value: string): void {
        const searchTerm = value.toLowerCase();
        this.filteredCurrencies = this.sourceCurrencies.filter(
            (currency) =>
                this.getCurrencyFullName(currency).toLowerCase().includes(searchTerm) ||
                currency.toLowerCase().includes(searchTerm)
        );

        // Validate input strictly
        const isExactMatch = this.filteredCurrencies.includes(value);
        const isValidSelection = this.filteredCurrencies.length > 0 && isExactMatch;

        if (value.trim() === '') {
            this.errorMessage.set(
                $localize`:@@currency-error-empty:Type to search or select a currency from the list.`
            );
            this.currency.setErrors({ invalid: true });
        } else if (!isValidSelection) {
            this.errorMessage.set($localize`:@@currency-error-invalid:There is no matching currency.`);
            this.currency.setErrors({ invalid: true });
        } else {
            this.currency.setErrors(null);
            this.errorMessage.set('');
        }

        // IMPORTANT: Do NOT change sourceCurrency here
    }

    /**
     * Updates the selected source currency and recalculates the exchange rate.
     * Also updates the input field to display the full name and code of the selected currency.
     * @param {any} event - The event triggered by selecting a source currency.
     * @returns {void}
     */
    onSourceCurrencySelected(event: any): void {
        const newCurrency = event.option.value;
        this.sourceCurrency = newCurrency; // Update source currency selection
        this.sourceCurrencyInput = this.getCurrencyFullNameWithCode(newCurrency); // Update the input field to display name and code
        this.calculateRate();
    }

    /**
     * Handles changes to the markup value.
     * Validates and updates the markup amount, then recalculates the exchange rate.
     * Logs an error if the new value is invalid.
     * @param {any} newValue - The new markup value entered by the user.
     * @returns {void}
     */
    onMarkupChange(newValue: any): void {
        const parsedValue = parseFloat(newValue);

        // Check if the input is null, undefined, or an invalid number
        if (newValue === null || newValue === '' || isNaN(parsedValue) || parsedValue < 0) {
            this.markup = 0; // Default to 0 for safety
        } else {
            this.markup = parsedValue; // Set the parsed value
        }

        this.calculateRate(); // Recalculate rate based on the current markup
    }

    /**
     * Handles the focus event on the input field.
     * Sets the filtered currencies to the list of source currencies.
     * @returns {void}
     */
    onInputFocus(): void {
        this.filteredCurrencies = this.sourceCurrencies;
    }

    /**
     * Prevents the user from entering invalid characters in the input field.
     * Blocks negative signs, plus signs, and exponential notation ('e').
     * @param {KeyboardEvent} event - The keyboard event triggered by the user's input.
     * @returns {void}
     */
    preventNegativeInput(event: KeyboardEvent): void {
        if (event.key === '-' || event.key === '+' || event.key === 'e') {
            event.preventDefault();
        }
    }

    /**
     * Opens a dialog box using the provided event.
     * Prevents the default action and configures the dialog with specific options.
     * @param {Event} evt - The event that triggers the dialog.
     * @returns {void}
     */
    openDialog(evt: Event): void {
        evt.preventDefault();
        this.dialog.open(this.dialogTemplate, { autoFocus: 'first-tabbable', panelClass: 'riege-dialog' });
    }

    /**
     * Toggles the visibility of the markup input field.
     * If hidden, resets the markup value to 0 and recalculates the rate.
     * @returns {void}
     */
    toggleMarkup(): void {
        this.markupVisible = !this.markupVisible;

        if (!this.markupVisible) {
            // Reset markup-specific calculations when hidden
            this.markup = 0;
            this.calculateRate();
        }
    }
}
